import React, { useEffect, useState } from "react";
import {
  getMyStats,
  getMyStatsComparison,
  getStatsShareLinks,
  createStatsShareLink,
  revokeStatsShareLink,
  setMyBirthYear,
} from "../../services/GameService";
import StatsDisplay, { formatNum } from "./StatsDisplay";
import "./MyStatsPage.css";

const shareUrlFor = (token) => `${window.location.origin}/shared/${token}`;

// Birth year is optional at sign-up. Without it there's no age group to
// compare with, so offer to add it here, where it matters.
const BirthYearPrompt = ({ onSaved }) => {
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = (e) => {
    e.preventDefault();
    const thisYear = new Date().getFullYear();
    if (!/^\d{4}$/.test(year) || Number(year) < 1900 || Number(year) > thisYear) {
      setError("Please enter a year like 1951.");
      return;
    }
    setBusy(true);
    setError("");
    setMyBirthYear(year)
      .then(onSaved)
      .catch(() => setError("We couldn't save that. Please try again."))
      .finally(() => setBusy(false));
  };
  return (
    <section className="ms-section">
      <h2 className="ms-section-title">How you compare</h2>
      <p className="ms-compare-sub">
        Add your birth year to see how your scores compare with players your age.
      </p>
      <form className="ms-birth-year" onSubmit={save}>
        <label htmlFor="ms-birth-year" className="ms-birth-year-label">
          Birth year
        </label>
        <input
          id="ms-birth-year"
          className="ms-share-url"
          inputMode="numeric"
          maxLength={4}
          placeholder="e.g. 1951"
          value={year}
          onChange={(e) => setYear(e.target.value.trim())}
        />
        <button type="submit" className="ms-btn ms-btn-primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
      {error && <p className="ms-error">{error}</p>}
    </section>
  );
};

const formatExpiry = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

// Patient Mode: a calm, encouraging view of the player's own progress.
// Broken down game by game and day by day so the numbers stay meaningful
// (scores aren't comparable across different games).
const MyStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [shareLinks, setShareLinks] = useState([]);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState("");
  const [copiedToken, setCopiedToken] = useState("");

  useEffect(() => {
    getMyStats()
      .then(setStats)
      .catch(() => setError("We couldn't load your progress right now."))
      .finally(() => setLoading(false));
    // Comparison is a bonus section — load it separately so a hiccup here
    // never blocks the main progress view.
    getMyStatsComparison()
      .then(setComparison)
      .catch(() => setComparison(null));
    getStatsShareLinks()
      .then((data) => setShareLinks(data.links || []))
      .catch(() => setShareLinks([]));
  }, []);

  const handleCreateLink = () => {
    setShareBusy(true);
    setShareError("");
    createStatsShareLink()
      .then((link) => setShareLinks((prev) => [link, ...prev]))
      .catch(() => setShareError("We couldn't create a link. Please try again."))
      .finally(() => setShareBusy(false));
  };

  const handleRevoke = (token) => {
    setShareError("");
    revokeStatsShareLink(token)
      .then(() =>
        setShareLinks((prev) => prev.filter((l) => l.token !== token))
      )
      .catch(() => setShareError("We couldn't revoke that link. Please try again."));
  };

  const handleCopy = (token) => {
    navigator.clipboard?.writeText(shareUrlFor(token)).then(
      () => {
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(""), 2000);
      },
      () => setShareError("Couldn't copy — please copy the link manually.")
    );
  };

  if (loading) {
    return (
      <div className="my-stats">
        <p>Loading your progress…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-stats">
        <p className="ms-error">{error}</p>
      </div>
    );
  }

  const message =
    stats.sessions_played === 0
      ? "Ready to start? Pick a game and have fun!"
      : "Great work — keep it up!";

  const comparisons = comparison?.comparisons || [];

  return (
    <div className="my-stats">
      <h1 className="ms-title">Your Progress</h1>
      <p className="ms-encourage">{message}</p>

      <StatsDisplay stats={stats} />

      {comparison?.needs_birth_year && (
        <BirthYearPrompt
          onSaved={() =>
            getMyStatsComparison()
              .then(setComparison)
              .catch(() => setComparison(null))
          }
        />
      )}

      {comparison?.age_band && (
        <section className="ms-section">
          <h2 className="ms-section-title">How you compare</h2>
          <p className="ms-compare-sub">
            Compared with other players aged {comparison.age_band}
          </p>
          {comparisons.length === 0 ? (
            <p className="ms-compare-pending">
              Play a game to see how you compare with your age group 🌱
            </p>
          ) : (
            <div className="ms-compare-grid">
              {comparisons.map((c) => {
                if (!c.enough_data) {
                  // Always shown, but disabled: no age-group peers have played
                  // this game yet, so there's nothing to compare against.
                  return (
                    <div
                      className="ms-compare-card ms-compare-disabled"
                      key={c.game_id}
                    >
                      <span className="ms-game-name">{c.game}</span>
                      <div className="ms-compare-rows">
                        <div className="ms-compare-row">
                          <span className="ms-compare-lbl">You</span>
                          <span className="ms-compare-val ms-you">
                            {formatNum(c.your_avg)}
                          </span>
                        </div>
                        <div className="ms-compare-row">
                          <span className="ms-compare-lbl">Your group</span>
                          <span className="ms-compare-val">—</span>
                        </div>
                      </div>
                      <span className="ms-compare-pending">
                        No one else in your age group has played this yet —
                        check back soon 🌱
                      </span>
                    </div>
                  );
                }
                const standing =
                  c.top_percent <= 50
                    ? `You're in the top ${c.top_percent}% of your age group 🌿`
                    : `You're ahead of ${c.percentile}% of your age group`;
                return (
                  <div className="ms-compare-card" key={c.game_id}>
                    <span className="ms-game-name">{c.game}</span>
                    <div className="ms-compare-rows">
                      <div className="ms-compare-row">
                        <span className="ms-compare-lbl">You</span>
                        <span className="ms-compare-val ms-you">
                          {formatNum(c.your_avg)}
                        </span>
                      </div>
                      <div className="ms-compare-row">
                        <span className="ms-compare-lbl">Your group</span>
                        <span className="ms-compare-val">{formatNum(c.group_avg)}</span>
                      </div>
                    </div>
                    <span
                      className={
                        c.above_average
                          ? "ms-compare-badge ms-above"
                          : "ms-compare-badge ms-below"
                      }
                    >
                      {c.above_average
                        ? "↑ Above your age group's average"
                        : "↓ Below your age group's average"}
                    </span>
                    <span className="ms-compare-standing">{standing}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <section className="ms-section">
        <h2 className="ms-section-title">Share with a clinician</h2>
        <p className="ms-compare-sub">
          Create a private, read-only link to your progress. Send it to your
          clinician — they don't need an account. You can revoke it anytime.
        </p>

        {shareError && <p className="ms-error">{shareError}</p>}

        <div className="ms-share">
          {shareLinks.map((link) => (
            <div className="ms-share-link" key={link.token}>
              <input
                className="ms-share-url"
                type="text"
                readOnly
                value={shareUrlFor(link.token)}
                onFocus={(e) => e.target.select()}
              />
              <div className="ms-share-actions">
                <button
                  type="button"
                  className="ms-btn ms-btn-primary"
                  onClick={() => handleCopy(link.token)}
                >
                  {copiedToken === link.token ? "Copied!" : "Copy link"}
                </button>
                <button
                  type="button"
                  className="ms-btn ms-btn-ghost"
                  onClick={() => handleRevoke(link.token)}
                >
                  Revoke
                </button>
              </div>
              <span className="ms-share-expiry">
                Expires {formatExpiry(link.expires_at)}
              </span>
            </div>
          ))}

          <button
            type="button"
            className="ms-btn ms-btn-primary ms-share-create"
            onClick={handleCreateLink}
            disabled={shareBusy}
          >
            {shareBusy ? "Creating…" : "Create a share link"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default MyStatsPage;
